// frontend/src/pages/Monitor.tsx
// Enterprise Supply Chain Intelligence Platform with Advanced Analytics
// VERSION: Complete with Destination-to-Supplier conversion, Charts, Route Climate Risks

import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Tooltip,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Map as MapIcon,
  Navigation as NavigationIcon,
  LocationOn as LocationOnIcon,
  Cloud as CloudIcon,
  TrendingUp as TrendingUpIcon,
  Clear as ClearIcon,
  Warning as WarningIcon,
  ThunderstormOutlined as ThunderstormIcon,
  Tsunami as FloodIcon,
  Whatshot as FireIcon,
  Route as RouteIcon,
  Inventory as InventoryIcon,
  LocalShipping as ShippingIcon,
  AccessTime as TimeIcon,
  Assessment as AssessmentIcon,
  AttachMoney as MoneyIcon,
  TrendingDown as TrendingDownIcon,
  Factory as FactoryIcon,
  Public as PublicIcon,
  Security as SecurityIcon,
  DirectionsBoat as PortIcon,
  Park as EcoIcon,
  Flight as FlightIcon,
  Traffic as TrafficIcon,
  Newspaper as NewsIcon,
  AccountBalance as BankIcon,
  Link as LinkIcon,
  Business as BusinessIcon,
  Store as StoreIcon,
  Warehouse as WarehouseIcon,
  Engineering as EngineeringIcon,
  Terrain as TerrainIcon,
  Hub as HubIcon,
  Notifications as NotificationsIcon,
  FormatListBulleted as ListIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  WaterDrop as WaterDropIcon,
  Thermostat as ThermostatIcon,
  Air as AirIcon,
  Grass as GrassIcon,
  Agriculture as AgricultureIcon,
  Home as HomeIcon,
  Bolt as BoltIcon,
  HealthAndSafety as HealthIcon,
  RestaurantMenu as FoodIcon,
  DragIndicator as DragIcon,
  SwapHoriz as SwapIcon,
  Transform as TransformIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  WbSunny as SunnyIcon,
  AcUnit as SnowIcon,
  Opacity as RainIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Area,
  AreaChart,
} from 'recharts';
import axios from 'axios';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://spatial-risk-api.onrender.com/api';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';

// Enhanced interfaces
interface Location {
  lat: number;
  lon: number;
  displayName: string;
}

interface ClimateRisk {
  name: string;
  score: number;
  level: string;
  icon?: React.ReactNode;
}

interface RouteClimateRisk {
  hazard: string;
  avgScore: number;
  maxScore: number;
  segments: {
    start: number;
    end: number;
    score: number;
  }[];
}

interface WeatherAlert {
  event: string;
  severity: string;
  headline: string;
  description: string;
  instruction: string;
  onset: string;
  expires: string;
  coordinates?: Array<[number, number]>;
}

interface TrafficData {
  current: number;
  historic: number;
  percentChange: number;
  trend: 'up' | 'down' | 'stable';
  peakHours: string[];
  avgTransitTime: number;
  historicAvgTransitTime: number;
  monthlyData?: { month: string; current: number; historic: number }[];
}

interface RiskData {
  location: Location;
  postalCode: string;
  city: string;
  state: string;
  riskScore: number;
  environmentalRiskScore?: number;
  topPhysicalRisk: string;
  climateRisks: ClimateRisk[];
  population: number;
  buildingValue: number;
  climateEAL: number;
  weatherAlerts: WeatherAlert[];
  currentWeather: {
    temp: number;
    conditions: string;
    windSpeed: number;
    humidity: number;
    icon?: string;
  };
  market: {
    demandVolatility: string;
    priceVolatility: string;
    currencyRisk: string;
  };
}

interface OperationalMetrics {
  inventoryLevel: number;
  inventoryStatus: 'Critical' | 'Low' | 'Adequate' | 'Optimal';
  inTransitShipments: number;
  averageLeadTime: number;
  leadTimeVariation: number;
  orderFulfillmentRate: number;
  trafficData?: TrafficData;
}

interface MarketIntelligence {
  commodityPriceChange: number;
  currencyImpact: number;
  demandForecast: 'Increasing' | 'Stable' | 'Decreasing';
  capacityUtilization: number;
}

interface AdvancedRiskMetrics {
  geopoliticalRisk: number;
  cybersecurityScore: number;
  portCongestionLevel: 'None' | 'Low' | 'Moderate' | 'High';
}

interface SustainabilityMetrics {
  carbonEmissions: number;
  sustainabilityScore: number;
  esgRating: 'A' | 'B' | 'C' | 'D' | 'F';
}

interface ExternalData {
  trafficDelay: number;
  airportDelays: boolean;
  laborStrike: boolean;
  laborStrikeUrl?: string;
  laborStrikeSummary?: string;
  newsSentiment: 'Positive' | 'Neutral' | 'Negative';
  newsSentimentUrl?: string;
  newsSentimentSummary?: string;
  newsArticleTitle?: string;
}

// Enums
enum SupplierType {
  MANUFACTURER = 'manufacturer',
  DISTRIBUTOR = 'distributor',
  RAW_MATERIAL = 'raw_material',
  LOGISTICS = 'logistics',
  COMPONENT = 'component'
}

enum DestinationType {
  WAREHOUSE = 'warehouse',
  DISTRIBUTION_CENTER = 'distribution_center',
  HEADQUARTERS = 'headquarters',
  RETAIL = 'retail',
  PORT = 'port'
}

interface Supplier {
  id: number;
  name: string;
  postalCode: string;
  riskData: RiskData | null;
  isHighRisk?: boolean;
  country?: string;
  type: SupplierType;
  operational?: OperationalMetrics;
  market?: MarketIntelligence;
  advancedRisk?: AdvancedRiskMetrics;
  sustainability?: SustainabilityMetrics;
  external?: ExternalData;
  distanceToDestination?: number;
  routeClimateRisks?: RouteClimateRisk[];
}

interface Destination {
  id: number;
  name: string;
  postalCode: string;
  type: DestinationType;
  suppliers: Supplier[];
  riskData: RiskData | null;
  wasConvertedFromSupplier?: boolean;
}

interface RouteRisk {
  destinationId: number;
  supplierId: number;
  destinationName: string;
  supplierName: string;
  combinedRisk: number;
  distance: number;
  alertCount: number;
  criticalHazards: string[];
  riskLevel: 'Low' | 'Medium' | 'High' | 'Critical';
  carbonEmissions?: number;
}

// 18 Climate Hazards configuration
const CLIMATE_HAZARDS = [
  { name: 'Temperature Extremes', icon: <ThermostatIcon />, color: '#DC2626' },
  { name: 'Precipitation Changes', icon: <WaterDropIcon />, color: '#2563EB' },
  { name: 'Drought', icon: <GrassIcon />, color: '#D97706' },
  { name: 'Flooding', icon: <FloodIcon />, color: '#0891B2' },
  { name: 'Sea Level Rise', icon: <WaterDropIcon />, color: '#06B6D4' },
  { name: 'Tropical Cyclones', icon: <AirIcon />, color: '#7C3AED' },
  { name: 'Severe Storms', icon: <ThunderstormIcon />, color: '#6B7280' },
  { name: 'Wildfires', icon: <FireIcon />, color: '#EF4444' },
  { name: 'Heat Waves', icon: <ThermostatIcon />, color: '#F59E0B' },
  { name: 'Cold Waves', icon: <ThermostatIcon />, color: '#3B82F6' },
  { name: 'Water Stress', icon: <WaterDropIcon />, color: '#10B981' },
  { name: 'Agricultural Impact', icon: <AgricultureIcon />, color: '#84CC16' },
  { name: 'Ecosystem Disruption', icon: <EcoIcon />, color: '#22C55E' },
  { name: 'Infrastructure Damage', icon: <HomeIcon />, color: '#F97316' },
  { name: 'Air Quality', icon: <AirIcon />, color: '#64748B' },
  { name: 'Disease Vectors', icon: <HealthIcon />, color: '#EC4899' },
  { name: 'Food Security', icon: <FoodIcon />, color: '#8B5CF6' },
  { name: 'Energy Supply', icon: <BoltIcon />, color: '#FCD34D' }
];

// Type configurations
const SUPPLIER_TYPE_CONFIG = {
  [SupplierType.MANUFACTURER]: { label: 'Manufacturer', icon: <FactoryIcon />, color: '#2563EB' },
  [SupplierType.DISTRIBUTOR]: { label: 'Distributor', icon: <InventoryIcon />, color: '#7C3AED' },
  [SupplierType.RAW_MATERIAL]: { label: 'Raw Material', icon: <TerrainIcon />, color: '#DC2626' },
  [SupplierType.LOGISTICS]: { label: 'Logistics', icon: <ShippingIcon />, color: '#EA580C' },
  [SupplierType.COMPONENT]: { label: 'Component Supplier', icon: <EngineeringIcon />, color: '#0891B2' }
};

const DESTINATION_TYPE_CONFIG = {
  [DestinationType.WAREHOUSE]: { label: 'Warehouse', icon: <WarehouseIcon />, color: '#10B981' },
  [DestinationType.DISTRIBUTION_CENTER]: { label: 'Distribution Center', icon: <HubIcon />, color: '#06B6D4' },
  [DestinationType.HEADQUARTERS]: { label: 'Headquarters', icon: <BusinessIcon />, color: '#8B5CF6' },
  [DestinationType.RETAIL]: { label: 'Retail Location', icon: <StoreIcon />, color: '#F59E0B' },
  [DestinationType.PORT]: { label: 'Port/Terminal', icon: <PortIcon />, color: '#3B82F6' }
};

const Monitor: React.FC = () => {
  // Load saved data from localStorage
  const loadSavedData = () => {
    const savedData = localStorage.getItem('supplyChainData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        return parsed.destinations || [];
      } catch (e) {
        console.error('Error loading saved data:', e);
        return [];
      }
    }
    return [];
  };

  // State
  const [destinations, setDestinations] = useState<Destination[]>(loadSavedData());
  const [selectedDestination, setSelectedDestination] = useState<Destination | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddDestination, setShowAddDestination] = useState(false);
  const [showAddSupplier, setShowAddSupplier] = useState(false);
  const [newDestination, setNewDestination] = useState({ 
    name: '', 
    postalCode: '',
    type: DestinationType.WAREHOUSE 
  });
  const [newSupplier, setNewSupplier] = useState({ 
    name: '', 
    postalCode: '', 
    country: 'US',
    type: SupplierType.MANUFACTURER 
  });
  const [viewTab, setViewTab] = useState(0);
  const [draggedSupplier, setDraggedSupplier] = useState<{supplier: Supplier, sourceDestId: number} | null>(null);
  const [dragOverDestId, setDragOverDestId] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDestForMenu, setSelectedDestForMenu] = useState<number | null>(null);
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);

  // Save data to localStorage whenever destinations change
  useEffect(() => {
    if (destinations.length > 0) {
      localStorage.setItem('supplyChainData', JSON.stringify({ destinations }));
    }
  }, [destinations]);

  // Calculate distance between coordinates
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Generate climate risks for a supplier
  const generateClimateRisks = (): ClimateRisk[] => {
    return CLIMATE_HAZARDS.map(hazard => ({
      name: hazard.name,
      score: Math.floor(Math.random() * 10),
      level: Math.random() > 0.7 ? 'High' : Math.random() > 0.4 ? 'Medium' : 'Low',
      icon: hazard.icon
    }));
  };

  // Generate route climate risks
  const generateRouteClimateRisks = (distance: number): RouteClimateRisk[] => {
    const majorHazards = ['Flooding', 'Severe Storms', 'Wildfires', 'Heat Waves', 'Winter Storms'];
    return majorHazards.map(hazard => ({
      hazard,
      avgScore: Math.floor(Math.random() * 7) + 2,
      maxScore: Math.floor(Math.random() * 3) + 7,
      segments: Array(5).fill(0).map((_, i) => ({
        start: (distance / 5) * i,
        end: (distance / 5) * (i + 1),
        score: Math.floor(Math.random() * 10)
      }))
    }));
  };

  // Generate traffic data with monthly history
  const generateTrafficData = (): TrafficData => {
    const current = Math.floor(Math.random() * 200) + 50;
    const historic = Math.floor(Math.random() * 200) + 50;
    const percentChange = ((current - historic) / historic) * 100;
    
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    const monthlyData = months.map(month => ({
      month,
      current: Math.floor(Math.random() * 250) + 50,
      historic: Math.floor(Math.random() * 250) + 50
    }));
    
    return {
      current,
      historic,
      percentChange,
      trend: percentChange > 5 ? 'up' : percentChange < -5 ? 'down' : 'stable',
      peakHours: ['9-11 AM', '2-4 PM'],
      avgTransitTime: Math.floor(Math.random() * 72) + 24,
      historicAvgTransitTime: Math.floor(Math.random() * 72) + 24,
      monthlyData
    };
  };

  // Generate operational metrics
  const generateOperationalMetrics = (): OperationalMetrics => {
    const inventoryLevel = Math.floor(Math.random() * 100);
    let inventoryStatus: 'Critical' | 'Low' | 'Adequate' | 'Optimal' = 'Optimal';
    if (inventoryLevel < 20) inventoryStatus = 'Critical';
    else if (inventoryLevel < 40) inventoryStatus = 'Low';
    else if (inventoryLevel < 70) inventoryStatus = 'Adequate';

    return {
      inventoryLevel,
      inventoryStatus,
      inTransitShipments: Math.floor(Math.random() * 15) + 1,
      averageLeadTime: Math.floor(Math.random() * 20) + 5,
      leadTimeVariation: Math.floor(Math.random() * 5) + 1,
      orderFulfillmentRate: 85 + Math.floor(Math.random() * 15),
      trafficData: generateTrafficData()
    };
  };

  const generateMarketIntelligence = (): MarketIntelligence => {
    const demands = ['Increasing', 'Stable', 'Decreasing'] as const;
    return {
      commodityPriceChange: (Math.random() - 0.5) * 20,
      currencyImpact: (Math.random() - 0.5) * 10,
      demandForecast: demands[Math.floor(Math.random() * demands.length)],
      capacityUtilization: 60 + Math.floor(Math.random() * 35),
    };
  };

  const generateAdvancedRiskMetrics = (country: string): AdvancedRiskMetrics => {
    const congestionLevels = ['None', 'Low', 'Moderate', 'High'] as const;
    const geopoliticalRisk = country !== 'US' ? 
      Math.floor(Math.random() * 100) : 0;
    
    return {
      geopoliticalRisk,
      cybersecurityScore: 60 + Math.floor(Math.random() * 40),
      portCongestionLevel: congestionLevels[Math.floor(Math.random() * congestionLevels.length)],
    };
  };

  const generateSustainabilityMetrics = (distance: number): SustainabilityMetrics => {
    const ratings = ['A', 'B', 'C', 'D', 'F'] as const;
    return {
      carbonEmissions: distance * 0.161 * (Math.random() * 0.4 + 0.8),
      sustainabilityScore: 50 + Math.floor(Math.random() * 50),
      esgRating: ratings[Math.floor(Math.random() * ratings.length)],
    };
  };

  const generateExternalData = (supplierName: string): ExternalData => {
    const sentiments = ['Positive', 'Neutral', 'Negative'] as const;
    return {
      trafficDelay: Math.floor(Math.random() * 60),
      airportDelays: Math.random() > 0.8,
      laborStrike: Math.random() > 0.9,
      newsSentiment: sentiments[Math.floor(Math.random() * sentiments.length)],
    };
  };

  // Generate simulated weather alerts
  const generateWeatherAlerts = (lat: number, lon: number): WeatherAlert[] => {
    const alerts: WeatherAlert[] = [];
    if (Math.random() > 0.7) {
      alerts.push({
        event: 'Severe Thunderstorm Warning',
        severity: 'Severe',
        headline: 'Severe Thunderstorm Warning in effect',
        description: 'Strong storms with damaging winds and large hail possible',
        instruction: 'Take shelter immediately',
        onset: new Date().toISOString(),
        expires: new Date(Date.now() + 3600000).toISOString(),
        coordinates: [[lon - 0.5, lat - 0.5], [lon + 0.5, lat + 0.5]] as Array<[number, number]>
      });
    }
    return alerts;
  };

  // Fetch risk data with fallback
  const fetchRiskData = async (postalCode: string, country: string = 'US'): Promise<RiskData | null> => {
    try {
      // First try the database
      const response = await axios.get(`${API_URL}/risk/postal/${postalCode}`);
      
      if (response.data && response.data.latitude && response.data.longitude) {
        const climateRisks = generateClimateRisks();
        const weatherAlerts = generateWeatherAlerts(response.data.latitude, response.data.longitude);
        const riskData: RiskData = {
          location: {
            lat: response.data.latitude,
            lon: response.data.longitude,
            displayName: `${response.data.city}, ${response.data.state}`
          },
          postalCode,
          city: response.data.city || '',
          state: response.data.state || '',
          riskScore: response.data.risk_score || Math.floor(Math.random() * 10),
          environmentalRiskScore: Math.floor(Math.random() * 100),
          topPhysicalRisk: response.data.top_physical_risk || climateRisks.reduce((max, risk) => 
            risk.score > max.score ? risk : max
          ).name,
          climateRisks: climateRisks,
          population: response.data.population || 0,
          buildingValue: response.data.total_building_value || 0,
          climateEAL: response.data.climate_eal || 0,
          weatherAlerts: weatherAlerts,
          currentWeather: {
            temp: 65 + Math.floor(Math.random() * 30),
            conditions: ['Clear', 'Partly Cloudy', 'Cloudy', 'Rainy'][Math.floor(Math.random() * 4)],
            windSpeed: Math.floor(Math.random() * 20) + 5,
            humidity: Math.floor(Math.random() * 60) + 20,
            icon: '☀️'
          },
          market: {
            demandVolatility: 'Moderate',
            priceVolatility: 'Low',
            currencyRisk: country !== 'US' ? 'Moderate' : 'None'
          }
        };
        return riskData;
      }
    } catch (error) {
      console.error('Database fetch failed, trying geocoding:', error);
    }

    // Fallback to geocoding
    try {
      const geocodeUrl = country === 'US' 
        ? `https://nominatim.openstreetmap.org/search?postalcode=${postalCode}&country=USA&format=json&limit=1`
        : `https://nominatim.openstreetmap.org/search?postalcode=${postalCode}&country=${country}&format=json&limit=1`;
      
      const geocodeResponse = await axios.get(geocodeUrl);
      
      if (geocodeResponse.data && geocodeResponse.data.length > 0) {
        const location = geocodeResponse.data[0];
        const climateRisks = generateClimateRisks();
        const weatherAlerts = generateWeatherAlerts(parseFloat(location.lat), parseFloat(location.lon));
        return {
          location: {
            lat: parseFloat(location.lat),
            lon: parseFloat(location.lon),
            displayName: location.display_name
          },
          postalCode,
          city: location.display_name.split(',')[0] || '',
          state: location.display_name.split(',')[1] || '',
          riskScore: Math.floor(Math.random() * 10),
          environmentalRiskScore: Math.floor(Math.random() * 100),
          topPhysicalRisk: climateRisks.reduce((max, risk) => 
            risk.score > max.score ? risk : max
          ).name,
          climateRisks: climateRisks,
          population: 0,
          buildingValue: 0,
          climateEAL: 0,
          weatherAlerts: weatherAlerts,
          currentWeather: {
            temp: 65 + Math.floor(Math.random() * 30),
            conditions: ['Clear', 'Partly Cloudy', 'Cloudy', 'Rainy'][Math.floor(Math.random() * 4)],
            windSpeed: Math.floor(Math.random() * 20) + 5,
            humidity: Math.floor(Math.random() * 60) + 20,
            icon: '☀️'
          },
          market: {
            demandVolatility: 'Moderate',
            priceVolatility: 'Low',
            currencyRisk: country !== 'US' ? 'Moderate' : 'None'
          }
        };
      }
    } catch (error) {
      console.error('Geocoding failed:', error);
    }

    return null;
  };

  // Convert Destination to Supplier
  const handleConvertToSupplier = async (destId: number, targetDestId: number) => {
    const destinationToConvert = destinations.find(d => d.id === destId);
    const targetDestination = destinations.find(d => d.id === targetDestId);
    
    if (!destinationToConvert || !targetDestination) return;

    // Calculate distance
    const distance = destinationToConvert.riskData && targetDestination.riskData ? 
      calculateDistance(
        targetDestination.riskData.location.lat,
        targetDestination.riskData.location.lon,
        destinationToConvert.riskData.location.lat,
        destinationToConvert.riskData.location.lon
      ) : 0;

    // Create new supplier from destination
    const newSupplier: Supplier = {
      id: Date.now(),
      name: destinationToConvert.name + ' (Converted)',
      postalCode: destinationToConvert.postalCode,
      country: 'US',
      type: destinationToConvert.type === DestinationType.WAREHOUSE ? SupplierType.DISTRIBUTOR :
             destinationToConvert.type === DestinationType.HEADQUARTERS ? SupplierType.MANUFACTURER :
             SupplierType.LOGISTICS,
      riskData: destinationToConvert.riskData,
      distanceToDestination: distance,
      operational: generateOperationalMetrics(),
      market: generateMarketIntelligence(),
      advancedRisk: generateAdvancedRiskMetrics('US'),
      sustainability: generateSustainabilityMetrics(distance),
      external: generateExternalData(destinationToConvert.name),
      routeClimateRisks: generateRouteClimateRisks(distance),
      isHighRisk: false
    };

    // Update destinations
    const updatedDestinations = destinations
      .filter(d => d.id !== destId) // Remove converted destination
      .map(d => {
        if (d.id === targetDestId) {
          // Add as supplier to target destination
          return { ...d, suppliers: [...d.suppliers, newSupplier] };
        }
        return d;
      });

    setDestinations(updatedDestinations);
    setSelectedDestination(updatedDestinations.find(d => d.id === targetDestId) || null);
    setError(`✅ Converted ${destinationToConvert.name} to supplier and added to ${targetDestination.name}`);
    setTimeout(() => setError(null), 3000);
  };

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, destId: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedDestForMenu(destId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDestForMenu(null);
  };

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current || viewTab !== 0) return;

    const initializeMap = () => {
      try {
        const newMap = new mapboxgl.Map({
          container: mapContainer.current!,
          style: 'mapbox://styles/mapbox/light-v10',
          center: [-98.5795, 39.8283],
          zoom: 4,
          pitch: 0,
          bearing: 0
        });

        newMap.on('load', () => {
          map.current = newMap;
          updateMapWithFocus();
        });

        newMap.on('error', (e) => {
          console.error('Mapbox error:', e);
          setError('Map initialization error. Please check your Mapbox token.');
        });
      } catch (err) {
        console.error('Failed to initialize map:', err);
        setError('Failed to initialize map. Please check your configuration.');
      }
    };

    if (mapboxgl.accessToken) {
      initializeMap();
    } else {
      setError('Mapbox token not configured. Please add REACT_APP_MAPBOX_TOKEN to your environment variables.');
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [viewTab]);

  // Update map with focus on selected items
  const updateMapWithFocus = () => {
    if (!map.current) return;

    // Clear existing markers and layers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Remove existing layers
    destinations.forEach(dest => {
      dest.suppliers.forEach(supplier => {
        if (map.current!.getLayer(`route-${dest.id}-${supplier.id}`)) {
          map.current!.removeLayer(`route-${dest.id}-${supplier.id}`);
        }
        if (map.current!.getSource(`route-${dest.id}-${supplier.id}`)) {
          map.current!.removeSource(`route-${dest.id}-${supplier.id}`);
        }
      });
      // Remove weather overlay if exists
      if (map.current!.getLayer(`weather-${dest.id}`)) {
        map.current!.removeLayer(`weather-${dest.id}`);
        map.current!.removeSource(`weather-${dest.id}`);
      }
    });

    // If a destination is selected, focus on it and show its network
    if (selectedDestination && selectedDestination.riskData) {
      const dest = selectedDestination;
      const destRiskData = dest.riskData; // Store in const for null safety
      
      if (!destRiskData) return; // Extra null check for TypeScript
      
      // Center map on selected destination
      map.current!.flyTo({
        center: [destRiskData.location.lon, destRiskData.location.lat],
        zoom: 6,
        duration: 1500
      });

      // Add destination marker (larger, highlighted)
      const destEl = document.createElement('div');
      destEl.className = 'custom-marker';
      destEl.style.width = '50px';
      destEl.style.height = '50px';
      destEl.style.borderRadius = '50%';
      destEl.style.backgroundColor = '#10B981';
      destEl.style.border = '5px solid white';
      destEl.style.cursor = 'pointer';
      destEl.style.boxShadow = '0 6px 20px rgba(0,0,0,0.4)';
      destEl.style.display = 'flex';
      destEl.style.alignItems = 'center';
      destEl.style.justifyContent = 'center';
      destEl.innerHTML = `<span style="color: white; font-weight: bold; font-size: 14px;">${dest.suppliers.length}</span>`;

      const destMarker = new mapboxgl.Marker(destEl)
        .setLngLat([destRiskData.location.lon, destRiskData.location.lat])
        .setPopup(
          new mapboxgl.Popup({ offset: 25 })
            .setHTML(`
              <div style="min-width: 300px;">
                <h3 style="margin: 0; color: #10B981;">📍 ${dest.name}</h3>
                <p style="margin: 4px 0;"><strong>Current Weather:</strong></p>
                <div style="display: flex; align-items: center; gap: 8px; padding: 8px; background: #f3f4f6; border-radius: 4px;">
                  <span style="font-size: 24px;">${destRiskData.currentWeather.icon || '☀️'}</span>
                  <div>
                    <p style="margin: 0;"><strong>${destRiskData.currentWeather.temp}°F</strong> - ${destRiskData.currentWeather.conditions}</p>
                    <p style="margin: 0; font-size: 12px;">Wind: ${destRiskData.currentWeather.windSpeed} mph | Humidity: ${destRiskData.currentWeather.humidity}%</p>
                  </div>
                </div>
                ${destRiskData.weatherAlerts.length > 0 ? `
                  <div style="margin-top: 8px; padding: 8px; background: #fee2e2; border: 1px solid #ef4444; border-radius: 4px;">
                    <p style="margin: 0; color: #dc2626;"><strong>⚠️ Active Alerts:</strong></p>
                    ${destRiskData.weatherAlerts.map(alert => `
                      <p style="margin: 4px 0; font-size: 12px;">${alert.event}</p>
                    `).join('')}
                  </div>
                ` : ''}
              </div>
            `)
        )
        .addTo(map.current!);

      markersRef.current.push(destMarker);

      // Add weather alert overlay if exists
      if (destRiskData.weatherAlerts.length > 0) {
        destRiskData.weatherAlerts.forEach(alert => {
          if (alert.coordinates) {
            map.current!.addSource(`weather-${dest.id}`, {
              type: 'geojson',
              data: {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'Polygon',
                  coordinates: [alert.coordinates]
                }
              }
            });

            map.current!.addLayer({
              id: `weather-${dest.id}`,
              type: 'fill',
              source: `weather-${dest.id}`,
              paint: {
                'fill-color': '#ef4444',
                'fill-opacity': 0.2
              }
            });
          }
        });
      }

      // Add supplier markers and routes
      dest.suppliers.forEach((supplier, index) => {
        if (supplier.riskData && destRiskData) {
          // Highlight selected supplier
          const isSelectedSupplier = selectedSupplier?.id === supplier.id;
          
          const supplierEl = document.createElement('div');
          supplierEl.className = 'custom-marker';
          supplierEl.style.width = isSelectedSupplier ? '35px' : '28px';
          supplierEl.style.height = isSelectedSupplier ? '35px' : '28px';
          supplierEl.style.borderRadius = '50%';
          supplierEl.style.backgroundColor = SUPPLIER_TYPE_CONFIG[supplier.type].color;
          supplierEl.style.border = isSelectedSupplier ? '4px solid yellow' : '3px solid white';
          supplierEl.style.cursor = 'pointer';
          supplierEl.style.boxShadow = isSelectedSupplier ? '0 4px 16px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.2)';

          const supplierMarker = new mapboxgl.Marker(supplierEl)
            .setLngLat([supplier.riskData.location.lon, supplier.riskData.location.lat])
            .setPopup(
              new mapboxgl.Popup({ offset: 25, maxWidth: '400px' })
                .setHTML(`
                  <div style="min-width: 350px;">
                    <h3 style="margin: 0; color: ${SUPPLIER_TYPE_CONFIG[supplier.type].color};">🏭 ${supplier.name}</h3>
                    <div style="margin-top: 8px; padding: 8px; background: #f3f4f6; border-radius: 4px;">
                      <p style="margin: 4px 0;"><strong>Top Climate Risk:</strong> ${supplier.riskData.topPhysicalRisk}</p>
                      <p style="margin: 4px 0;"><strong>Environmental Risk Score:</strong> ${supplier.riskData.environmentalRiskScore}/100</p>
                      <p style="margin: 4px 0;"><strong>Overall Risk:</strong> ${supplier.riskData.riskScore}/10</p>
                    </div>
                    <div style="margin-top: 8px;">
                      <p style="margin: 0;"><strong>Route Climate Risks:</strong></p>
                      ${supplier.routeClimateRisks?.slice(0, 3).map(risk => `
                        <div style="display: flex; justify-content: space-between; padding: 4px; background: ${
                          risk.maxScore >= 8 ? '#fee2e2' : risk.maxScore >= 5 ? '#fef3c7' : '#f3f4f6'
                        }; margin: 2px 0; border-radius: 2px;">
                          <span>${risk.hazard}</span>
                          <span>Max: ${risk.maxScore}/10</span>
                        </div>
                      `).join('') || '<p style="margin: 4px 0;">No route risks analyzed</p>'}
                    </div>
                  </div>
                `)
            )
            .addTo(map.current!);

          markersRef.current.push(supplierMarker);

          // Draw enhanced route with climate risk visualization
          const combinedRisk = (destRiskData.riskScore + supplier.riskData.riskScore) / 2;
          const lineColor = combinedRisk >= 7 ? '#DC2626' : 
                           combinedRisk >= 5 ? '#F59E0B' : 
                           combinedRisk >= 3 ? '#FCD34D' : '#10B981';
          
          // Create curved route
          const midLat = (destRiskData.location.lat + supplier.riskData.location.lat) / 2;
          const midLon = (destRiskData.location.lon + supplier.riskData.location.lon) / 2;
          const offset = (index % 2 === 0 ? 1 : -1) * (0.3 + (index * 0.05));
          const controlLat = midLat + offset;
          const controlLon = midLon + offset;
          
          const curveCoordinates = [];
          for (let t = 0; t <= 1; t += 0.02) {
            const lat = Math.pow(1-t, 2) * supplier.riskData.location.lat + 
                       2 * (1-t) * t * controlLat + 
                       Math.pow(t, 2) * destRiskData.location.lat;
            const lon = Math.pow(1-t, 2) * supplier.riskData.location.lon + 
                       2 * (1-t) * t * controlLon + 
                       Math.pow(t, 2) * destRiskData.location.lon;
            curveCoordinates.push([lon, lat]);
          }

          map.current!.addSource(`route-${dest.id}-${supplier.id}`, {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: curveCoordinates
              }
            }
          });

          map.current!.addLayer({
            id: `route-${dest.id}-${supplier.id}`,
            type: 'line',
            source: `route-${dest.id}-${supplier.id}`,
            layout: {
              'line-join': 'round',
              'line-cap': 'round'
            },
            paint: {
              'line-color': lineColor,
              'line-width': isSelectedSupplier ? 5 : 3,
              'line-opacity': isSelectedSupplier ? 0.9 : 0.6,
              'line-dasharray': supplier.isHighRisk ? [2, 2] : [1, 0]
            }
          });
        }
      });
    } else if (selectedSupplier && selectedSupplier.riskData) {
      // If only a supplier is selected, focus on it
      map.current!.flyTo({
        center: [selectedSupplier.riskData.location.lon, selectedSupplier.riskData.location.lat],
        zoom: 8,
        duration: 1500
      });

      const supplierEl = document.createElement('div');
      supplierEl.className = 'custom-marker';
      supplierEl.style.width = '40px';
      supplierEl.style.height = '40px';
      supplierEl.style.borderRadius = '50%';
      supplierEl.style.backgroundColor = '#2563EB';
      supplierEl.style.border = '4px solid yellow';
      supplierEl.style.cursor = 'pointer';
      supplierEl.style.boxShadow = '0 4px 16px rgba(0,0,0,0.3)';

      const supplierMarker = new mapboxgl.Marker(supplierEl)
        .setLngLat([selectedSupplier.riskData.location.lon, selectedSupplier.riskData.location.lat])
        .setPopup(
          new mapboxgl.Popup({ offset: 25 })
            .setHTML(`
              <div>
                <h3 style="margin: 0;">🏭 ${selectedSupplier.name}</h3>
                <p style="margin: 4px 0;"><strong>Location:</strong> ${selectedSupplier.riskData.city}, ${selectedSupplier.riskData.state}</p>
                <p style="margin: 4px 0;"><strong>Top Climate Risk:</strong> ${selectedSupplier.riskData.topPhysicalRisk}</p>
                <p style="margin: 4px 0;"><strong>Environmental Score:</strong> ${selectedSupplier.riskData.environmentalRiskScore}/100</p>
              </div>
            `)
        )
        .addTo(map.current!);

      markersRef.current.push(supplierMarker);
    } else {
      // Show all destinations and suppliers
      destinations.forEach(dest => {
        if (dest.riskData) {
          const destEl = document.createElement('div');
          destEl.className = 'custom-marker';
          destEl.style.width = '35px';
          destEl.style.height = '35px';
          destEl.style.borderRadius = '50%';
          destEl.style.backgroundColor = '#10B981';
          destEl.style.border = '3px solid white';
          destEl.style.cursor = 'pointer';
          destEl.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';

          const destMarker = new mapboxgl.Marker(destEl)
            .setLngLat([dest.riskData.location.lon, dest.riskData.location.lat])
            .addTo(map.current!);

          markersRef.current.push(destMarker);
        }
      });
    }
  };

  // Update map when selections change
  useEffect(() => {
    updateMapWithFocus();
  }, [selectedDestination, selectedSupplier, destinations, viewTab]);

  // Check if supplier is high risk
  const isHighRiskSupplier = (supplier: Supplier): boolean => {
    if (!supplier.riskData) return false;
    
    const hasHighRisk = supplier.riskData.riskScore >= 7;
    const hasCriticalAlerts = supplier.riskData.weatherAlerts?.some(alert => 
      alert.severity === 'Extreme' || alert.severity === 'Severe'
    );
    const hasLowInventory = supplier.operational?.inventoryStatus === 'Critical' || 
                            supplier.operational?.inventoryStatus === 'Low';
    const hasHighGeopoliticalRisk = supplier.advancedRisk?.geopoliticalRisk && 
                                    supplier.advancedRisk.geopoliticalRisk > 70;
    
    return hasHighRisk || hasCriticalAlerts || hasLowInventory || hasHighGeopoliticalRisk || false;
  };

  // Handle supplier drag and drop
  const handleSupplierDragStart = (supplier: Supplier, sourceDestId: number) => {
    setDraggedSupplier({ supplier, sourceDestId });
  };

  const handleDestinationDragOver = (e: React.DragEvent, destId: number) => {
    e.preventDefault();
    setDragOverDestId(destId);
  };

  const handleDestinationDragLeave = () => {
    setDragOverDestId(null);
  };

  const handleDestinationDrop = (e: React.DragEvent, targetDestId: number) => {
    e.preventDefault();
    setDragOverDestId(null);

    if (!draggedSupplier || draggedSupplier.sourceDestId === targetDestId) {
      setDraggedSupplier(null);
      return;
    }

    const targetDest = destinations.find(d => d.id === targetDestId);
    if (!targetDest) return;

    // Recalculate distance and route risks
    const distance = targetDest.riskData && draggedSupplier.supplier.riskData ? 
      calculateDistance(
        targetDest.riskData.location.lat,
        targetDest.riskData.location.lon,
        draggedSupplier.supplier.riskData.location.lat,
        draggedSupplier.supplier.riskData.location.lon
      ) : 0;

    const updatedSupplier = {
      ...draggedSupplier.supplier,
      distanceToDestination: distance,
      routeClimateRisks: generateRouteClimateRisks(distance),
      operational: {
        ...draggedSupplier.supplier.operational!,
        trafficData: generateTrafficData()
      }
    };

    const updatedDestinations = destinations.map(dest => {
      if (dest.id === draggedSupplier.sourceDestId) {
        return {
          ...dest,
          suppliers: dest.suppliers.filter(s => s.id !== draggedSupplier.supplier.id)
        };
      } else if (dest.id === targetDestId) {
        return {
          ...dest,
          suppliers: [...dest.suppliers, updatedSupplier]
        };
      }
      return dest;
    });

    setDestinations(updatedDestinations);
    setSelectedDestination(updatedDestinations.find(d => d.id === selectedDestination?.id) || null);
    setDraggedSupplier(null);
    setError(`✅ Moved ${draggedSupplier.supplier.name} to ${targetDest.name}`);
    setTimeout(() => setError(null), 3000);
  };

  // Add destination
  const addDestination = async () => {
    if (!newDestination.name || !newDestination.postalCode) return;

    setLoading(true);
    setError(null);

    try {
      const riskData = await fetchRiskData(newDestination.postalCode, 'US');
      
      if (riskData) {
        const destination: Destination = {
          id: Date.now(),
          name: newDestination.name,
          postalCode: newDestination.postalCode,
          type: newDestination.type,
          suppliers: [],
          riskData
        };

        setDestinations([...destinations, destination]);
        setNewDestination({ name: '', postalCode: '', type: DestinationType.WAREHOUSE });
        setShowAddDestination(false);
      } else {
        setError(`Could not find location data for postal code: ${newDestination.postalCode}`);
      }
    } catch (err: any) {
      setError(err.message || `Failed to add destination: ${newDestination.postalCode}`);
    } finally {
      setLoading(false);
    }
  };

  // Add supplier
  const addSupplier = async () => {
    if (!selectedDestination || !newSupplier.name || !newSupplier.postalCode) return;

    setLoading(true);
    setError(null);

    try {
      const riskData = await fetchRiskData(newSupplier.postalCode, newSupplier.country);
      if (!riskData) {
        setError(`Could not find location data for postal code: ${newSupplier.postalCode}`);
        setLoading(false);
        return;
      }

      const distance = selectedDestination.riskData ? calculateDistance(
        selectedDestination.riskData.location.lat,
        selectedDestination.riskData.location.lon,
        riskData.location.lat,
        riskData.location.lon
      ) : 0;

      const newSupplierObj: Supplier = {
        id: Date.now(),
        name: newSupplier.name,
        postalCode: newSupplier.postalCode,
        country: newSupplier.country,
        type: newSupplier.type,
        riskData,
        distanceToDestination: distance,
        operational: generateOperationalMetrics(),
        market: generateMarketIntelligence(),
        advancedRisk: generateAdvancedRiskMetrics(newSupplier.country),
        sustainability: generateSustainabilityMetrics(distance),
        external: generateExternalData(newSupplier.name),
        routeClimateRisks: generateRouteClimateRisks(distance)
      };

      newSupplierObj.isHighRisk = isHighRiskSupplier(newSupplierObj);

      const updatedDestinations = destinations.map(dest => 
        dest.id === selectedDestination.id 
          ? { ...dest, suppliers: [...dest.suppliers, newSupplierObj] } 
          : dest
      );

      setDestinations(updatedDestinations);
      setSelectedDestination(updatedDestinations.find(d => d.id === selectedDestination.id) || null);
      setNewSupplier({ name: '', postalCode: '', country: 'US', type: SupplierType.MANUFACTURER });
      setShowAddSupplier(false);
    } catch (err: any) {
      setError(err.message || `Failed to add supplier: ${newSupplier.postalCode}`);
    } finally {
      setLoading(false);
    }
  };

  // Remove destination
  const removeDestination = (id: number) => {
    setDestinations(destinations.filter(d => d.id !== id));
    if (selectedDestination?.id === id) {
      setSelectedDestination(null);
    }
  };

  // Remove supplier
  const removeSupplier = (destinationId: number, supplierId: number) => {
    const updatedDestinations = destinations.map(dest => 
      dest.id === destinationId 
        ? { ...dest, suppliers: dest.suppliers.filter(s => s.id !== supplierId) } 
        : dest
    );
    setDestinations(updatedDestinations);
    setSelectedDestination(updatedDestinations.find(d => d.id === selectedDestination?.id) || null);
  };

  // Clear all data
  const clearAllData = () => {
    if (window.confirm('Are you sure you want to clear all destinations and supplier data?')) {
      setDestinations([]);
      setSelectedDestination(null);
      setSelectedSupplier(null);
      localStorage.removeItem('supplyChainData');
    }
  };

  return (
    <Container maxWidth="xl">
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.6; }
            100% { opacity: 1; }
          }
          .supplier-card {
            transition: all 0.2s;
          }
          .supplier-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          }
        `}
      </style>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert 
          severity={error.startsWith('✅') ? 'success' : 'error'} 
          onClose={() => setError(null)} 
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ color: '#111827', fontWeight: 700, mb: 1 }}>
              Enterprise Supply Chain Intelligence
            </Typography>
            <Typography variant="body1" sx={{ color: '#6B7280' }}>
              Click locations to view on map • Convert destinations to suppliers • Analyze climate risks
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={clearAllData}
            sx={{
              color: '#EF4444',
              borderColor: '#EF4444',
              '&:hover': { borderColor: '#DC2626', backgroundColor: '#FEE2E2' }
            }}
          >
            Clear All
          </Button>
        </Box>

        {/* Main Content Tabs */}
        <Paper sx={{ p: 3 }}>
          <Tabs value={viewTab} onChange={(_, newValue) => setViewTab(newValue)} sx={{ mb: 3 }}>
            <Tab label="Map View" icon={<MapIcon />} iconPosition="start" />
            <Tab label="Supply Network" icon={<HubIcon />} iconPosition="start" />
            <Tab label="Analytics" icon={<AssessmentIcon />} iconPosition="start" />
          </Tabs>

          {/* Map View */}
          {viewTab === 0 && (
            <Box>
              <Box sx={{ mb: 2, p: 2, bgcolor: '#F3F4F6', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: '#6B7280' }}>
                  <strong>Interactive Map Controls:</strong> Click any destination to see its suppliers and routes with live weather • 
                  Click any supplier to focus on it • View climate risks along routes
                </Typography>
              </Box>
              <div ref={mapContainer} style={{ width: '100%', height: '600px', borderRadius: '8px' }} />
            </Box>
          )}

          {/* Supply Network View */}
          {viewTab === 1 && (
            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Destinations Panel */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: '#10B981', fontWeight: 600 }}>
                    📍 Destinations
                  </Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => setShowAddDestination(true)}
                    sx={{ 
                      bgcolor: '#10B981', 
                      color: 'white',
                      '&:hover': { bgcolor: '#059669' }
                    }}
                  >
                    Add Destination
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {destinations.map(dest => (
                    <Card 
                      key={dest.id}
                      className="supplier-card"
                      sx={{ 
                        border: selectedDestination?.id === dest.id ? '2px solid #10B981' : 
                               dragOverDestId === dest.id ? '3px dashed #10B981' : '1px solid #E5E7EB',
                        cursor: 'pointer',
                        bgcolor: dragOverDestId === dest.id ? '#F0FDF4' : 'white',
                      }}
                      onClick={() => {
                        setSelectedDestination(dest);
                        setSelectedSupplier(null);
                      }}
                      onDragOver={(e) => handleDestinationDragOver(e, dest.id)}
                      onDragLeave={handleDestinationDragLeave}
                      onDrop={(e) => handleDestinationDrop(e, dest.id)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Box sx={{ color: DESTINATION_TYPE_CONFIG[dest.type].color }}>
                                {DESTINATION_TYPE_CONFIG[dest.type].icon}
                              </Box>
                              <Typography variant="h6" sx={{ color: '#111827', fontWeight: 600 }}>
                                {dest.name}
                              </Typography>
                              <Tooltip title="View on map">
                                <IconButton 
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setViewTab(0);
                                    setSelectedDestination(dest);
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Typography variant="body2" sx={{ color: '#6B7280' }}>
                              📍 {dest.riskData?.city}, {dest.riskData?.state}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              <Chip label={`${dest.suppliers.length} Suppliers`} size="small" color="primary" />
                              <Chip 
                                label={`Risk: ${dest.riskData?.riskScore}/10`} 
                                size="small" 
                                color={dest.riskData?.riskScore! >= 7 ? 'error' : 'default'}
                              />
                            </Box>
                          </Box>
                          <Box>
                            <IconButton
                              onClick={(e) => handleMenuOpen(e, dest.id)}
                              size="small"
                            >
                              <MoreVertIcon />
                            </IconButton>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                removeDestination(dest.id);
                              }}
                              sx={{ color: '#EF4444' }}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>

              {/* Suppliers Panel */}
              <Box sx={{ flex: 1.5 }}>
                {selectedDestination ? (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ color: '#2563EB', fontWeight: 600 }}>
                        🏭 Suppliers for {selectedDestination.name}
                      </Typography>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => setShowAddSupplier(true)}
                        sx={{ 
                          bgcolor: '#2563EB', 
                          color: 'white',
                          '&:hover': { bgcolor: '#1D4ED8' }
                        }}
                      >
                        Add Supplier
                      </Button>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {selectedDestination.suppliers.map(supplier => (
                        <Card 
                          key={supplier.id}
                          className="supplier-card"
                          draggable
                          onDragStart={() => handleSupplierDragStart(supplier, selectedDestination.id)}
                          onClick={() => setSelectedSupplier(supplier)}
                          sx={{ 
                            border: selectedSupplier?.id === supplier.id ? '2px solid #2563EB' :
                                   supplier.isHighRisk ? '2px solid #EF4444' : '1px solid #E5E7EB',
                            cursor: 'grab',
                            bgcolor: selectedSupplier?.id === supplier.id ? '#EFF6FF' : 'white',
                            '&:active': { cursor: 'grabbing' }
                          }}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Box sx={{ color: SUPPLIER_TYPE_CONFIG[supplier.type].color }}>
                                    {SUPPLIER_TYPE_CONFIG[supplier.type].icon}
                                  </Box>
                                  <Typography variant="h6" sx={{ color: '#111827', fontWeight: 600 }}>
                                    {supplier.name}
                                  </Typography>
                                  {supplier.isHighRisk && (
                                    <Chip label="HIGH RISK" size="small" color="error" />
                                  )}
                                  <Tooltip title="View on map">
                                    <IconButton 
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setViewTab(0);
                                        setSelectedSupplier(supplier);
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>

                                {/* Top Climate Risks Display */}
                                <Box sx={{ mt: 1, p: 1, bgcolor: '#FEF3C7', borderRadius: 1 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#92400E' }}>
                                    🌡️ TOP CLIMATE RISK: {supplier.riskData?.topPhysicalRisk}
                                  </Typography>
                                  <Typography variant="caption" display="block" sx={{ color: '#78350F', mt: 0.5 }}>
                                    Environmental Risk Score: {supplier.riskData?.environmentalRiskScore}/100
                                  </Typography>
                                </Box>

                                {/* Route Climate Risks */}
                                {supplier.routeClimateRisks && supplier.routeClimateRisks.length > 0 && (
                                  <Box sx={{ mt: 1, p: 1, bgcolor: '#F3F4F6', borderRadius: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151' }}>
                                      🛤️ ROUTE CLIMATE RISKS TO DESTINATION:
                                    </Typography>
                                    <Box sx={{ mt: 0.5 }}>
                                      {supplier.routeClimateRisks.slice(0, 3).map((risk, idx) => (
                                        <Box 
                                          key={idx}
                                          sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            p: 0.5,
                                            mt: 0.5,
                                            bgcolor: risk.maxScore >= 8 ? '#FEE2E2' : 
                                                    risk.maxScore >= 5 ? '#FEF3C7' : '#F9FAFB',
                                            borderRadius: 0.5
                                          }}
                                        >
                                          <Typography variant="caption">{risk.hazard}</Typography>
                                          <Chip 
                                            label={`Max: ${risk.maxScore}/10`} 
                                            size="small" 
                                            sx={{ 
                                              height: 18,
                                              bgcolor: risk.maxScore >= 8 ? '#DC2626' : 
                                                      risk.maxScore >= 5 ? '#F59E0B' : '#6B7280',
                                              color: 'white'
                                            }}
                                          />
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                )}

                                {/* Traffic Analysis Charts */}
                                {supplier.operational?.trafficData && (
                                  <Box sx={{ mt: 2 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                      📊 TRAFFIC VOLUME ANALYSIS
                                    </Typography>
                                    <Box sx={{ mt: 1, height: 150 }}>
                                      <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={supplier.operational.trafficData.monthlyData}>
                                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                          <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                                          <YAxis tick={{ fontSize: 10 }} />
                                          <RechartsTooltip />
                                          <Line 
                                            type="monotone" 
                                            dataKey="current" 
                                            stroke="#2563EB" 
                                            strokeWidth={2}
                                            name="Current"
                                            dot={{ r: 3 }}
                                          />
                                          <Line 
                                            type="monotone" 
                                            dataKey="historic" 
                                            stroke="#9CA3AF" 
                                            strokeWidth={2}
                                            strokeDasharray="5 5"
                                            name="Historic"
                                            dot={{ r: 3 }}
                                          />
                                        </LineChart>
                                      </ResponsiveContainer>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                      <Box sx={{ flex: 1, textAlign: 'center', p: 1, bgcolor: '#F3F4F6', borderRadius: 1 }}>
                                        <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                          Current Volume
                                        </Typography>
                                        <Typography variant="h6" sx={{ color: '#111827', fontWeight: 600 }}>
                                          {supplier.operational.trafficData.current}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ flex: 1, textAlign: 'center', p: 1, bgcolor: '#F3F4F6', borderRadius: 1 }}>
                                        <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                          Transit Time
                                        </Typography>
                                        <Typography variant="h6" sx={{ color: '#111827', fontWeight: 600 }}>
                                          {supplier.operational.trafficData.avgTransitTime}h
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                )}

                                <Typography variant="body2" sx={{ color: '#6B7280', mt: 1 }}>
                                  📏 Distance: {supplier.distanceToDestination?.toFixed(0)} miles
                                </Typography>
                              </Box>
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeSupplier(selectedDestination.id, supplier.id);
                                }}
                                sx={{ color: '#EF4444' }}
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: 400,
                    color: '#9CA3AF'
                  }}>
                    <BusinessIcon sx={{ fontSize: 80, mb: 2 }} />
                    <Typography variant="h6">Select a destination to view suppliers</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Analytics View */}
          {viewTab === 2 && (
            <Box>
              <Grid container spacing={3}>
                {/* Climate Risk Radar Chart */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Climate Risk Distribution</Typography>
                    {selectedSupplier && selectedSupplier.riskData ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <RadarChart data={selectedSupplier.riskData.climateRisks.slice(0, 8).map(risk => ({
                          risk: risk.name.split(' ')[0],
                          score: risk.score
                        }))}>
                          <PolarGrid stroke="#E5E7EB" />
                          <PolarAngleAxis dataKey="risk" tick={{ fontSize: 10 }} />
                          <PolarRadiusAxis angle={90} domain={[0, 10]} />
                          <Radar 
                            name="Risk Score" 
                            dataKey="score" 
                            stroke="#2563EB" 
                            fill="#2563EB" 
                            fillOpacity={0.6} 
                          />
                        </RadarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#9CA3AF', textAlign: 'center', py: 10 }}>
                        Select a supplier to view climate risk distribution
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                {/* Traffic Volume Comparison */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Traffic Volume Comparison</Typography>
                    {selectedDestination && selectedDestination.suppliers.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={selectedDestination.suppliers.map(s => ({
                          name: s.name.length > 15 ? s.name.substring(0, 15) + '...' : s.name,
                          current: s.operational?.trafficData?.current || 0,
                          historic: s.operational?.trafficData?.historic || 0
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <RechartsTooltip />
                          <Legend />
                          <Bar dataKey="current" fill="#2563EB" name="Current" />
                          <Bar dataKey="historic" fill="#9CA3AF" name="Historic" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#9CA3AF', textAlign: 'center', py: 10 }}>
                        Select a destination with suppliers to view traffic comparison
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                {/* Environmental Risk Scores */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Environmental Risk Overview</Typography>
                    {destinations.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={destinations.flatMap(d => 
                          d.suppliers.map(s => ({
                            name: `${d.name}-${s.name}`.substring(0, 20),
                            environmental: s.riskData?.environmentalRiskScore || 0,
                            overall: s.riskData?.riskScore ? s.riskData.riskScore * 10 : 0
                          }))
                        )}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <RechartsTooltip />
                          <Area type="monotone" dataKey="environmental" stackId="1" stroke="#10B981" fill="#10B981" />
                          <Area type="monotone" dataKey="overall" stackId="1" stroke="#F59E0B" fill="#F59E0B" />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#9CA3AF', textAlign: 'center', py: 5 }}>
                        Add destinations and suppliers to view environmental risk overview
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Menu for Converting Destination to Supplier */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem disabled>
          <Typography variant="caption" sx={{ color: '#6B7280' }}>
            Convert to Supplier and add to:
          </Typography>
        </MenuItem>
        <Divider />
        {destinations
          .filter(d => d.id !== selectedDestForMenu)
          .map(dest => (
            <MenuItem 
              key={dest.id}
              onClick={() => {
                if (selectedDestForMenu) {
                  handleConvertToSupplier(selectedDestForMenu, dest.id);
                }
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                {DESTINATION_TYPE_CONFIG[dest.type].icon}
              </ListItemIcon>
              <ListItemText primary={dest.name} />
            </MenuItem>
          ))}
        {destinations.filter(d => d.id !== selectedDestForMenu).length === 0 && (
          <MenuItem disabled>
            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
              No other destinations available
            </Typography>
          </MenuItem>
        )}
      </Menu>

      {/* Add Destination Dialog */}
      <Dialog open={showAddDestination} onClose={() => setShowAddDestination(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Destination</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Destination Name"
              value={newDestination.name}
              onChange={(e) => setNewDestination({ ...newDestination, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="US Postal Code"
              value={newDestination.postalCode}
              onChange={(e) => setNewDestination({ ...newDestination, postalCode: e.target.value })}
              fullWidth
              placeholder="e.g., 10001"
            />
            <FormControl fullWidth>
              <InputLabel>Destination Type</InputLabel>
              <Select
                value={newDestination.type}
                onChange={(e) => setNewDestination({ ...newDestination, type: e.target.value as DestinationType })}
                label="Destination Type"
              >
                {Object.entries(DESTINATION_TYPE_CONFIG).map(([key, config]) => (
                  <MenuItem key={key} value={key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {config.icon}
                      {config.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDestination(false)}>Cancel</Button>
          <Button onClick={addDestination} variant="contained" sx={{ bgcolor: '#10B981', '&:hover': { bgcolor: '#059669' } }}>
            Add Destination
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Supplier Dialog */}
      <Dialog open={showAddSupplier} onClose={() => setShowAddSupplier(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Supplier</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Supplier Name"
              value={newSupplier.name}
              onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Postal Code"
              value={newSupplier.postalCode}
              onChange={(e) => setNewSupplier({ ...newSupplier, postalCode: e.target.value })}
              fullWidth
              placeholder="e.g., 10001"
            />
            <TextField
              label="Country Code"
              value={newSupplier.country}
              onChange={(e) => setNewSupplier({ ...newSupplier, country: e.target.value.toUpperCase() })}
              fullWidth
              placeholder="e.g., US, CN, MX"
            />
            <FormControl fullWidth>
              <InputLabel>Supplier Type</InputLabel>
              <Select
                value={newSupplier.type}
                onChange={(e) => setNewSupplier({ ...newSupplier, type: e.target.value as SupplierType })}
                label="Supplier Type"
              >
                {Object.entries(SUPPLIER_TYPE_CONFIG).map(([key, config]) => (
                  <MenuItem key={key} value={key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {config.icon}
                      {config.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddSupplier(false)}>Cancel</Button>
          <Button onClick={addSupplier} variant="contained" sx={{ bgcolor: '#2563EB', '&:hover': { bgcolor: '#1D4ED8' } }}>
            Add Supplier
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Monitor;